import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import { tzAbbreviation } from "@/lib/datetime"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Converts a string to title case (first letter of each word capitalized)
 */
export function toTitleCase(text: string): string {
  if (!text) return '';
  return text
    .toLowerCase()
    .split(' ')
    .map(word => {
      if (!word) return word;
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
}

/**
 * Formats time elapsed in a human-readable way
 * Progression: X seconds ago -> X minutes ago -> X hours ago -> X days ago -> X weeks ago -> X months ago -> X years ago
 */
export function formatTimeAgo(startTime: string | Date, endTime?: string | Date): string {
  const start = new Date(startTime);
  const end = endTime ? new Date(endTime) : new Date();

  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return 'Unknown time';
  }

  const elapsedMs = end.getTime() - start.getTime();
  const elapsedSeconds = Math.floor(elapsedMs / 1000);
  const elapsedMinutes = Math.floor(elapsedMs / (1000 * 60));

  if (elapsedMinutes < 1) {
    return `${elapsedSeconds} second${elapsedSeconds !== 1 ? 's' : ''} ago`;
  }

  if (elapsedMinutes < 60) {
    return `${elapsedMinutes} minute${elapsedMinutes !== 1 ? 's' : ''} ago`;
  }

  const elapsedHours = Math.floor(elapsedMinutes / 60);
  if (elapsedHours < 24) {
    return `${elapsedHours} hour${elapsedHours !== 1 ? 's' : ''} ago`;
  }

  const elapsedDays = Math.floor(elapsedMinutes / (60 * 24));
  if (elapsedDays < 7) {
    return `${elapsedDays} day${elapsedDays !== 1 ? 's' : ''} ago`;
  }

  if (elapsedDays < 30) {
    const elapsedWeeks = Math.floor(elapsedDays / 7);
    return `${elapsedWeeks} week${elapsedWeeks !== 1 ? 's' : ''} ago`;
  }

  const elapsedMonths = Math.floor(elapsedDays / 30);
  if (elapsedMonths < 12) {
    return `${elapsedMonths} month${elapsedMonths !== 1 ? 's' : ''} ago`;
  }

  const elapsedYears = Math.floor(elapsedMonths / 12);
  return `${elapsedYears} year${elapsedYears !== 1 ? 's' : ''} ago`;
}

/**
 * Formats elapsed time from a start time to now
 */
export function formatElapsedTime(startTime: string | Date): string {
  return formatTimeAgo(startTime);
}

/**
 * Extract a human-readable message from an unknown error.
 * @param err - The caught error (unknown type)
 * @param fallback - Fallback message when err is not an Error instance (default: stringifies err)
 */
export function getErrorMessage(err: unknown, fallback?: string): string {
  if (err instanceof Error) return err.message;
  return fallback ?? String(err);
}

/**
 * Extract a truncated error message suitable for sending to analytics (e.g. PostHog).
 * Truncates to maxLength to avoid sending large payloads to third-party services.
 */
export function getSafeErrorForAnalytics(err: unknown, maxLength = 200): string {
  const raw = err instanceof Error ? err.message : String(err);
  return raw.length > maxLength ? raw.slice(0, maxLength) + '…' : raw;
}

/**
 * Formats a date string to a consistent, readable format
 * @param dateString - ISO date string or Date-compatible string
 * @returns Formatted date string (e.g., "Oct 15, 2024") or "Unknown" if invalid
 */
export function formatDate(dateString: string | undefined | null): string {
  if (!dateString) return 'Unknown';

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'Unknown';

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Gets the ordinal suffix for a number (1st, 2nd, 3rd, 4th, etc.)
 * @param day - The day number
 * @returns The ordinal suffix (st, nd, rd, th)
 */
function getOrdinalSuffix(day: number): string {
  if (day >= 11 && day <= 13) {
    return 'th';
  }
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
}

/**
 * Formats a date string with ordinal day format
 * @param dateString - ISO date string or Date-compatible string
 * @returns Formatted date string (e.g., "6th Nov 2025") or "Unknown" if invalid
 */
export function formatDateWithOrdinal(dateString: string | undefined | null): string {
  if (!dateString) return 'Unknown';

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'Unknown';

  const day = date.getDate();
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const year = date.getFullYear();
  const ordinalSuffix = getOrdinalSuffix(day);

  return `${day}${ordinalSuffix} ${month} ${year}`;
}

/**
 * Formats a file size in bytes to a human-readable string (e.g., "1.5 MB")
 * @param bytes - The file size in bytes
 * @returns Formatted file size string
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + sizes[i];
}

export function formatTimezone(tz: string): string {
  try {
    // e.g. "IST (Asia/Kolkata)" — the abbreviation makes the zone recognisable,
    // and we keep the IANA name in parens so colliding abbreviations (IST, CST,
    // AST) stay unambiguous.
    // preferStandard so a static settings card doesn't flip EST/EDT seasonally.
    return `${tzAbbreviation(tz, new Date(), { preferStandard: true })} (${tz})`;
  } catch {
    return tz;
  }
}

/** Allauth's `display` returns the bare username (e.g. "aviroop"). When the
 *  session payload lacks first_name/last_name, title-case the username (or
 *  the local-part of the email) so reader-friendly chips show "Aviroop" not
 *  "aviroop". Idempotent on already-pretty names. */
export function prettifyDisplay(raw: string | undefined | null): string {
  if (!raw) return '';
  const base = raw.includes('@') ? raw.split('@')[0] : raw;
  return base
    .replace(/[._-]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

/** Resolve a friendly label for an internal user (recruiter / reviewer /
 *  added_by). Always prettifies — turns "aviroop" → "Aviroop" and
 *  "recruiter@recruiter.com" → "Recruiter". Use ONLY for app users; never on
 *  candidate display names (candidate emails should stay raw). */
export function internalUserName(
  user: { name?: string | null; email?: string | null } | null | undefined,
  fallback = 'Unknown',
): string {
  if (!user) return fallback;
  return prettifyDisplay(user.name) || prettifyDisplay(user.email) || fallback;
}
